import { useCallback, useEffect, useState } from "react";
import { guildApi, type GuildAltarSpinResult, type GuildAltarStatus } from "../../api/guild";
import { RouletteBoard } from "../RouletteBoard";
import type { RouletteSegmentPreview, RouletteSpinCost } from "../../api/roulette";
import { refreshHeroIfMountStatsChanged } from "../../store/useMountsStore";

const SKIP_KEY = "mk_guild_altar_skip_animation";

export function GuildAltarPanel() {
  const [status, setStatus] = useState<GuildAltarStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<GuildAltarSpinResult | null>(null);
  const [pendingWinSlot, setPendingWinSlot] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [skipAnimation, setSkipAnimation] = useState(
    () => localStorage.getItem(SKIP_KEY) === "1",
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await guildApi.altarStatus();
      setStatus(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load altar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSpin = async (): Promise<number> => {
    setShowResult(false);
    setPendingWinSlot(null);
    setLastResult(null);
    setSpinning(true);
    try {
      const result = await guildApi.altarSpin();
      setLastResult(result);
      setStatus((s: GuildAltarStatus | null) =>
        s ? { ...s, free_spin_available: result.free_spin_available } : s,
      );
      await refreshHeroIfMountStatsChanged({ rewardType: result.reward.type });
      setPendingWinSlot(result.winning_slot_index);
      return result.winning_slot_index;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Spin failed");
      throw e;
    } finally {
      setSpinning(false);
    }
  };

  if (loading && !status) {
    return <p className="text-sm text-gray-400 text-center py-6">Loading altar…</p>;
  }

  if (!status?.in_guild) {
    return (
      <p className="text-sm text-gray-400 text-center py-6">
        Join a guild to use the altar roulette.
      </p>
    );
  }

  const segments = status.segments.map((s) => ({
    slot: s.slot,
    reward_preview: s.reward_preview,
  })) as RouletteSegmentPreview[];

  const fakeCost: RouletteSpinCost = { type: "consumable", item_id: "altar", amount: 1 };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="font-bold text-gold-400 font-game">Guild Altar</h2>
        <p className="text-xs text-gray-400 mt-1">
          {status.free_spin_available
            ? "Free daily spin available"
            : "Free spin used today — come back tomorrow"}
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      <RouletteBoard
        segments={segments}
        spinCost={fakeCost}
        costAvailable={status.free_spin_available ? 1 : 0}
        spinning={spinning || pendingWinSlot !== null}
        skipAnimation={skipAnimation}
        pendingWinSlot={pendingWinSlot}
        onSpin={handleSpin}
        onAnimationComplete={() => {
          setPendingWinSlot(null);
          setShowResult(true);
        }}
        spinLabel="КРУТИТЬ"
        hideCostHint
      />

      <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer justify-center">
        <input
          type="checkbox"
          checked={skipAnimation}
          onChange={(e) => {
            setSkipAnimation(e.target.checked);
            localStorage.setItem(SKIP_KEY, e.target.checked ? "1" : "0");
          }}
          className="rounded border-kingdom-mid"
        />
        Пропустить анимацию
      </label>

      {showResult && lastResult && (
        <div className="rounded-xl border border-gold-500/50 bg-kingdom-purple p-4 text-center space-y-2">
          <div className="text-3xl">🎉</div>
          <p className="font-bold text-gold-400">{lastResult.message}</p>
        </div>
      )}
    </div>
  );
}
